import I18n from '../../config/i18n';
import ToggleSection from '../../utils/toggleSection/ToggleSection';
import DetailsList, { DetailsListData } from './DetailsList';
import DetailsTable from './DetailsTable';
import DetailsReviews from './DetailsReviews';
import ReviewHeaderLine from '../../features/accommodationReviews/components/ReviewHeaderLine';
import BreakfastOffer from './BreakfastOffer';

import './HostDetails.css';

const createInnerHTML = (__html: string): { __html: string } => ({ __html });

/* eslint-disable camelcase */

interface DetailsTableProps {
  category: string;
  features: string[];
}

// Basic Info

interface BasicInfoProps {
  description_html?: string;
  info?: DetailsListData[];
  hasBreakfast: boolean;
}

function BasicInfo({ description_html, info, hasBreakfast }: BasicInfoProps): JSX.Element {
  return (
    <ToggleSection
      label={I18n.t('host.info')}
      className="host-details host-details-info"
      actAsToggle={false}
    >
      {description_html != null ? (
        <div
          className="host-details-description"
          dangerouslySetInnerHTML={createInnerHTML(description_html)}
        />
      ) : null}
      {hasBreakfast ? <BreakfastOffer /> : null}
      <DetailsList data={info} />
    </ToggleSection>
  );
}

const basicInfoDefaultProps: Pick<BasicInfoProps, 'description_html' | 'info'> = {
  description_html: undefined,
  info: [],
};

BasicInfo.defaultProps = basicInfoDefaultProps;

// Amenities

interface AmenitiesProps {
  amenities?: DetailsTableProps[];
}

function Amenities({ amenities }: AmenitiesProps): JSX.Element {
  return (
    <ToggleSection
      label={I18n.t('host.amenities')}
      className="host-details host-details-amenities"
      actAsToggle={false}
    >
      <DetailsTable data={amenities} />
    </ToggleSection>
  );
}

const amenitiesDefaultProps: Pick<AmenitiesProps, 'amenities'> = {
  amenities: [],
};

Amenities.defaultProps = amenitiesDefaultProps;

// Costs

interface CostProps {
  costs?: DetailsListData[];
  includedCosts?: DetailsListData[];
  extraOptions?: DetailsListData[];
}

function Costs({ costs, includedCosts, extraOptions }: CostProps): JSX.Element {
  return (
    <ToggleSection
      label={I18n.t('host.additional_costs')}
      className="host-details host-details-costs"
      actAsToggle={false}
    >
      <DetailsList data={[...(costs || []), ...(extraOptions || []), ...(includedCosts || [])]} />
    </ToggleSection>
  );
}

const costsDefaultProps: Pick<CostProps, 'costs' | 'extraOptions' | 'includedCosts'> = {
  costs: [],
  extraOptions: [],
  includedCosts: [],
};

Costs.defaultProps = costsDefaultProps;

// Accommodation Reviews

interface AccommodationReviewsProps {
  showReviews: boolean;
}

function AccommodationReviews({ showReviews }: AccommodationReviewsProps): JSX.Element | null {
  if (!showReviews) {
    return null;
  }

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/anchor-has-content */}
      <a id="reviews" className="anchor-header-aware" />
      <ToggleSection
        label={<ReviewHeaderLine />}
        className="host-details host-details-reviews"
        actAsToggle={false}
      >
        <DetailsReviews />
      </ToggleSection>
    </>
  );
}

// Extra Options

interface ExtraOptionsProps {
  extraCosts?: DetailsListData[];
}

function ExtraOptions({ extraCosts }: ExtraOptionsProps): JSX.Element | null {
  if (extraCosts && extraCosts.length < 1) {
    return null;
  }
  return (
    <ToggleSection
      label={I18n.t('activerecord.attributes.extra.optional')}
      className="host-details host-details-extra-options"
      actAsToggle={false}
    >
      <DetailsList data={[...(extraCosts || [])]} />
    </ToggleSection>
  );
}

const extraOptionsDefaultProps: Pick<ExtraOptionsProps, 'extraCosts'> = {
  extraCosts: [],
};

ExtraOptions.defaultProps = extraOptionsDefaultProps;

// Attractions

interface AttractionsProps {
  attractions?: DetailsTableProps[];
}

function Attractions({ attractions }: AttractionsProps): JSX.Element {
  return (
    <ToggleSection
      label={I18n.t('host.attractions')}
      className="host-details host-details-attractions"
      actAsToggle={false}
    >
      <DetailsTable data={attractions} />
    </ToggleSection>
  );
}

const attractionsDefaultProps: Pick<AttractionsProps, 'attractions'> = {
  attractions: [],
};

Attractions.defaultProps = attractionsDefaultProps;

export default { BasicInfo, Amenities, Costs, AccommodationReviews, ExtraOptions, Attractions };
